<template>
  <div>
    <div class="btn-wrapper" :class="styleType">
      <ui-button
        v-for="tab in tabs"
        :key="tab.key"
        class="btn-list"
        :class="{
          'active-styles': tab.key === activeTab,
        }"
        :icon="tab.icon"
        :text="tab.label"
        :button-style="tab.key === activeTab ? activeButtonStyle : 'default'"
        @click="switchTab(tab.key)"
      />
    </div>
    <div v-for="tab in tabs" v-show="tab.key === activeTab" :key="tab.key">
      <slot :name="tabBodySlotName(tab.key)" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';

export interface UiTabsItem {
  key: string;
  label: string;
  icon?: string;
}

const props = withDefaults(
  defineProps<{
    tabs: UiTabsItem[];
    activeButtonStyle?: string;
    styleType?: string;
  }>(),
  {
    tabs: () => [],
    activeButtonStyle: 'accent',
    styleType: 'style-button',
  },
);

const emit = defineEmits<{
  switchTab: [tabKey: string];
}>();

const activeTab = ref(props.tabs[0]?.key ?? '');

watch(
  () => props.tabs,
  (newTabs) => {
    const keys = newTabs.map((item) => item.key);
    if (!keys.length) {
      activeTab.value = '';
      return;
    }
    if (!keys.includes(activeTab.value)) {
      activeTab.value = keys[0] ?? '';
    }
  },
  { deep: true },
);

const tabBodySlotName = (key: string) => `content-${key}`;

const switchTab = (tabKey: string) => {
  activeTab.value = tabKey;
  emit('switchTab', tabKey);
};
</script>

<style lang="scss" scoped>
.btn-wrapper {
  display: flex;
  flex-shrink: 0;
  & + & {
    margin-left: 0 !important;
  }
}

.style-button {
  margin-bottom: 8px;

  .btn-list {
    margin: 0;

    &:not(:last-child) :deep(.ui-button) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    &:not(:first-child) :deep(.ui-button) {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
}

.style-list {
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--color-gray20);

  .btn-wrapper :deep(.ui-button:hover:before) {
    background-color: transparent !important;
  }

  .btn-list {
    &:first-child {
      margin-left: 0;
      padding-left: 0;
    }

    :deep(.ui-button) {
      border: none !important;
      background-color: transparent !important;
      background-image: none !important;

      .text {
        text-transform: capitalize;
        font-size: 20px;
        color: var(--color-blue);
      }
    }

    @media (max-width: 600px) {
      height: 26px;
      padding: 0;
      :deep(.ui-button .text) {
        font-size: 15px;
        line-height: 20px;
      }
    }
  }
  .active-styles :deep(.ui-button) {
    .text {
      color: var(--color-graphite) !important;
    }

    .content {
      position: relative;

      &:before {
        content: '';
        position: absolute;
        bottom: -3px;
        min-width: 100%;
        border-bottom: 4px solid var(--color-blue);
      }
    }
  }
}
</style>
